import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./auth-context";

interface ScopeValue {
  projectId: string | null;
  brandId: string | null;
  setProjectId: (id: string) => void;
  setBrandId: (id: string | null) => void;
  clearScope: () => void;
}

interface StoredScope {
  projectId: string | null;
  brandId: string | null;
}

interface ScopeSources {
  urlProjectId: string | null;
  urlBrandId: string | null;
  persistedProjectId: string | null;
  persistedBrandId: string | null;
  cleared: boolean;
}

const STORAGE_KEY = "nexus:scope";

const ScopeContext = createContext<ScopeValue>({
  projectId: null,
  brandId: null,
  setProjectId: () => {},
  setBrandId: () => {},
  clearScope: () => {},
});

function normalizeScopeId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function readPersistedScope(): StoredScope {
  if (typeof window === "undefined") {
    return { projectId: null, brandId: null };
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return { projectId: null, brandId: null };

    const parsed = JSON.parse(stored) as Partial<
      Record<keyof StoredScope, unknown>
    >;
    return {
      projectId: normalizeScopeId(parsed.projectId),
      brandId: normalizeScopeId(parsed.brandId),
    };
  } catch {
    return { projectId: null, brandId: null };
  }
}

function loadScopeSources(): ScopeSources {
  if (typeof window === "undefined") {
    return scopeSourcesFromEffective(null, null, false);
  }

  const params = new URLSearchParams(window.location.search);
  const urlProjectId = normalizeScopeId(params.get("project"));
  const persisted = readPersistedScope();

  return {
    urlProjectId,
    urlBrandId: urlProjectId ? normalizeScopeId(params.get("brand")) : null,
    persistedProjectId: persisted.projectId,
    persistedBrandId: persisted.brandId,
    cleared: false,
  };
}

function scopeSourcesFromEffective(
  projectId: string | null,
  brandId: string | null,
  cleared: boolean,
): ScopeSources {
  const normalizedBrandId = projectId ? brandId : null;

  return {
    urlProjectId: projectId,
    urlBrandId: normalizedBrandId,
    persistedProjectId: projectId,
    persistedBrandId: normalizedBrandId,
    cleared,
  };
}

function areScopeSourcesEqual(left: ScopeSources, right: ScopeSources) {
  return (
    left.urlProjectId === right.urlProjectId &&
    left.urlBrandId === right.urlBrandId &&
    left.persistedProjectId === right.persistedProjectId &&
    left.persistedBrandId === right.persistedBrandId &&
    left.cleared === right.cleared
  );
}

function persistScope(projectId: string | null, brandId: string | null) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ projectId, brandId }),
    );
  } catch {
    // ignore
  }

  // Update URL without navigation
  const url = new URL(window.location.href);
  if (projectId) {
    url.searchParams.set("project", projectId);
  } else {
    url.searchParams.delete("project");
  }
  if (brandId) {
    url.searchParams.set("brand", brandId);
  } else {
    url.searchParams.delete("brand");
  }
  window.history.replaceState({}, "", url.toString());
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { allowedProjectIds, startup, isLoading, isLoggedIn } = useAuth();
  const [requestedScope, setRequestedScope] = useState<ScopeSources>(() =>
    loadScopeSources(),
  );

  const authorizedProjectIds = useMemo(() => {
    const ids = new Set(allowedProjectIds);
    for (const id of Object.keys(startup?.project_roles ?? {})) {
      ids.add(id);
    }
    return ids;
  }, [allowedProjectIds, startup]);

  const isProjectAuthorized = useCallback(
    (id: string | null): id is string => !!id && authorizedProjectIds.has(id),
    [authorizedProjectIds],
  );

  const defaultProjectId = useMemo(() => {
    const startupDefaultProjectId =
      startup?.default_project_id ?? startup?.default_project?.id ?? null;
    if (isProjectAuthorized(startupDefaultProjectId))
      return startupDefaultProjectId;

    const firstAllowedProjectId = allowedProjectIds.find((id) =>
      isProjectAuthorized(id),
    );
    if (firstAllowedProjectId) return firstAllowedProjectId;

    return (
      Object.keys(startup?.project_roles ?? {}).find((id) =>
        isProjectAuthorized(id),
      ) ?? null
    );
  }, [allowedProjectIds, isProjectAuthorized, startup]);

  const effectiveScope = useMemo((): StoredScope => {
    if (requestedScope.cleared) {
      return { projectId: null, brandId: null };
    }

    if (isProjectAuthorized(requestedScope.urlProjectId)) {
      return {
        projectId: requestedScope.urlProjectId,
        brandId: requestedScope.urlBrandId,
      };
    }

    if (isProjectAuthorized(requestedScope.persistedProjectId)) {
      return {
        projectId: requestedScope.persistedProjectId,
        brandId: requestedScope.persistedBrandId,
      };
    }

    if (defaultProjectId) {
      return { projectId: defaultProjectId, brandId: null };
    }

    return { projectId: null, brandId: null };
  }, [defaultProjectId, isProjectAuthorized, requestedScope]);

  // Persist the normalized effective scope after auth data has validated it.
  useEffect(() => {
    if (isLoading || !isLoggedIn) return;

    persistScope(effectiveScope.projectId, effectiveScope.brandId);
    setRequestedScope((current) => {
      const next = scopeSourcesFromEffective(
        effectiveScope.projectId,
        effectiveScope.brandId,
        current.cleared && !effectiveScope.projectId,
      );
      return areScopeSourcesEqual(current, next) ? current : next;
    });
  }, [effectiveScope.brandId, effectiveScope.projectId, isLoading, isLoggedIn]);

  const setProjectId = useCallback(
    (id: string) => {
      if (!isProjectAuthorized(id)) return;

      setRequestedScope(scopeSourcesFromEffective(id, null, false));
      persistScope(id, null);
    },
    [isProjectAuthorized],
  );

  const setBrandId = useCallback(
    (id: string | null) => {
      if (!effectiveScope.projectId) {
        setRequestedScope((current) =>
          scopeSourcesFromEffective(null, null, current.cleared),
        );
        persistScope(null, null);
        return;
      }

      setRequestedScope(
        scopeSourcesFromEffective(effectiveScope.projectId, id, false),
      );
      persistScope(effectiveScope.projectId, id);
    },
    [effectiveScope.projectId],
  );

  const clearScope = useCallback(() => {
    setRequestedScope(scopeSourcesFromEffective(null, null, true));
    persistScope(null, null);
  }, []);

  return (
    <ScopeContext.Provider
      value={{
        projectId: effectiveScope.projectId,
        brandId: effectiveScope.brandId,
        setProjectId,
        setBrandId,
        clearScope,
      }}
    >
      {children}
    </ScopeContext.Provider>
  );
}

export function useScope() {
  return useContext(ScopeContext);
}
